import { DisplayedAttachment } from '@fullyops/shared/attachment-set';
import { AssetClass } from '../asset-types/asset-type';

export const assetCriticalities = [
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
] as const;

export type AssetCriticality = (typeof assetCriticalities)[number];

export interface BaseAssetInput {
  name: string;
  active: boolean;
  companyId: string | null;
  locationId: string | null;
  description: string;
  criticality: AssetCriticality | null;
  warrantyExpiresAt: string | null;
  installedAt: string | null;
  externalId: string;
  categoryId: string | null;
  parentId: string | null;
  typeId: string | null;
  classSpecificProperties:
    | StandaloneEquipmentInput
    | EquipmentGroupInput
    | null;
  typeSpecificProperties: { [key: string]: string };
}

export interface AssetInput extends BaseAssetInput {
  attachmentIds: string[];
}

export interface Asset extends BaseAssetInput {
  id: string;
  attachments: AssetAttachment[];
}

export interface StandaloneEquipmentInput {
  brand: string;
  model: string;
  serialNumber: string;
  equipmentType: string;
  manufacturedAt: string | null;
}

// Equipment groups have no specific properties.
export type EquipmentGroupInput = object;

export interface AssetAttachment {
  id: string;
  fileName: string;
  url: string;
}

export function toDisplayedAttachment(
  at: AssetAttachment,
): DisplayedAttachment {
  return {
    pending: false,
    id: at.id,
    label: at.fileName,
    file: {
      fileName: at.fileName,
      signedUrl: at.url,
    },
  };
}

export type AssetOwnership = 'UNASSIGNED' | 'CLIENT_OWNED';

export interface MiniAsset {
  id: string;
  name: string;
  // needed for asset pickers that display this
  serialNumber: string;
  /**
   * Whether this asset is unassigned or already assigned to the client. Not part of the API
   * response: it is filled in by AssetsRepository.refreshUnassigned and refreshInstallable, which
   * know each row's assignment from the call it came back on. Left undefined on every other path,
   * where the question is not being asked and no label should be shown.
   */
  ownership?: AssetOwnership;
}

interface BaseListedAsset {
  id: string;
  name: string;
  externalId: string;
  companyId: string;
  locationId: string;
  installedAt: string;
  warrantyExpiresAt: string;
  active: boolean;
  assetClass: AssetClass;
  typeId: string;
  categoryId: string | null;
}

export interface ListedStandaloneEquipment extends BaseListedAsset {
  assetClass: 'STANDALONE_EQUIPMENT';
  classSpecificProperties: StandaloneEquipmentInput;
}

export interface ListedEquipmentGroup extends BaseListedAsset {
  assetClass: 'EQUIPMENT_GROUP';
  classSpecificProperties: EquipmentGroupInput;
}

export type ListedAsset = ListedStandaloneEquipment | ListedEquipmentGroup;
